/**
 * Kumo Functions（/p/:projectId/:env/functions）: 状態・URL・公開中の版・上限・DB と保存先・定期実行・API キーやパスワード・デプロイの履歴。
 * サーバー: GET/POST/PUT …/environments/:env/functions[/enable|/disable|/limits|/deployments|/secrets/:name]（modules/compute/routes.ts・service.ts）。
 *
 * デプロイ・SQL は CLI / MCP の仕事（AI ネイティブ運用）なので、画面は「見る」と「オン / オフ・上限」が中心。
 * 例外は API キーやパスワード（秘密）の値: 2026-10-04 console/secret-input（founder y）から、この画面で入れる・上書き・消すのが既定の道
 *   （components/FunctionSecretsCard.tsx）。WHY: AI のチャットに貼ると会話の記録に残る。値は入れた後も表示しない（Kumo は値を保存しない）。
 * 既定 OFF（MASTER-PLAN「全部を取捨選択できる」）。オンにするには前払い残高が要る（402 prepaid_required → 前払いの画面へ案内）。
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, envPath } from '../api';
import { ago, bytes, ConfirmDialog, ErrorState, fullDate, PageHeader, Spinner } from '../components/ui';
import { usePagedResource, useResource } from '../hooks';
import { LoadMore } from '../components/LoadMore';
import { errorText, formatNumber, translate, useT, type MessageKey } from '../i18n';
import type { FunctionsDeployment, FunctionsStatus } from '../moneyApi';
import { useShell } from './ProjectShell';
import { isPrepaid402 } from '../prepaidGate';
import { CLI } from '../cliCommand';
import { projectEyebrow } from '../projectNav';
import { balanceShortfall, limitInputRange, limitsFormStart, type LimitRanges } from '../functionsLimits';
import { money } from '../money';
import { FunctionSecretsCard } from '../components/FunctionSecretsCard';
import { useIsOwner } from '../components/OwnerOnly';

const KIND_KEYS: Record<string, MessageKey> = { d1: 'fn.kind.d1', kv: 'fn.kind.kv', r2: 'fn.kind.r2', queue: 'fn.kind.queue' };
const kindLabel = (k: string) => (KIND_KEYS[k] ? translate(KIND_KEYS[k]) : k);

export function FunctionsPage() {
  const { project, env } = useShell();
  const isOwner = useIsOwner();
  const base = `${envPath(project.id, env)}/functions`;
  const st = useResource<FunctionsStatus>(base);
  // 20 件ずつ「さらに読み込む」で古い版へ（2026-10-04 console/limits-ui。以前は 20 件で切れて、それより古い版が見えなかった）
  const deps = usePagedResource<{ deployments: FunctionsDeployment[]; nextCursor?: string | null }, 'deployments'>(`${base}/deployments?limit=20`, 'deployments');
  const { t, tr } = useT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmOff, setConfirmOff] = useState(false);
  const envName = t(`env.inline.${env}`);
  useEffect(() => {
    setNotice(null);
    setErr(null);
  }, [base]);

  const s = st.data;
  const state: 'unavailable' | 'paused' | 'on' | 'off' | null = !s ? null : !s.available ? 'unavailable' : s.suspended ? 'paused' : s.enabled ? 'on' : 'off';
  // CLI の functions は既定 development（packages/cli/src/commands/functions.ts）。production へは --env を明示する
  const envFlag = env === 'production' ? ' --env production' : '';
  const cliCmd = `${CLI} functions deploy${envFlag}`;

  const toggle = async (on: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      await api(`${base}/${on ? 'enable' : 'disable'}`, { method: 'POST', body: {} });
      setNotice(on ? t('fn.turnedOn') : t('fn.turnedOff'));
      setConfirmOff(false);
      st.reload();
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader eyebrow={projectEyebrow(project)} title={t('fn.title')}>
        {state && <span className={`pill ${state === 'on' ? 'pill-ok' : state === 'paused' ? 'pill-danger' : ''}`}>{t(`fn.state.${state}`)}</span>}
        {/* 有効 / 無効・上限は持ち主だけ（費用と止める操作 = 2026-10-22 core/handoff-1b・サーバーの表）。共同作業者はデプロイ・ログ・D1・秘密を入れる */}
        {state === 'off' && isOwner && (
          <button type="button" className="btn btn-primary" onClick={() => void toggle(true)} disabled={busy}>
            {busy ? t('common.working') : t('fn.turnOn')}
          </button>
        )}
        {state === 'on' && isOwner && (
          <button type="button" className="btn btn-ghost" onClick={() => setConfirmOff(true)} disabled={busy}>
            {t('fn.turnOff')}
          </button>
        )}
      </PageHeader>
      <p className="page-lead muted">{t('fn.lead')}</p>
      {st.loading && !s && <Spinner />}
      {st.error && <ErrorState error={st.error} what={t('fn.title')} onRetry={st.reload} />}
      {notice && (
        <div className="alert alert-ok" role="status">
          {notice}
        </div>
      )}
      {err && (
        <div className="alert alert-error" role="alert">
          {/* Functions の 402 は 2026-09-27 に prepaid_required → balance_due に変わった（Core の E2E-07・details.legacyCode 付き）。
              balance_due の共通の文言は「利用料が未払い」で、まだ入金したことが無い人には合わない。Functions の画面では
              「先に前払い残高に入金」の文言（error.prepaid_required）を出す（残高 0 以下のどちらの場合にも合う） */}
          <span>{errorText(err, { balance_due: 'error.prepaid_required' })}</span>
          {isPrepaid402(err) && (
            <Link to="/money/prepaid" className="btn btn-sm btn-ghost">
              {t('pp.topUp')}
            </Link>
          )}
        </div>
      )}

      {state === 'unavailable' && (
        <div className="alert alert-info" role="note">
          <strong>{t('fn.unavailableTitle')}</strong>
          <span>{t('fn.unavailableBody')}</span>
        </div>
      )}
      {state === 'off' && (
        <div className="alert alert-info" role="note">
          <span>{tr('fn.offBody', { env: envName, cmd: <code>{`${CLI} functions enable${envFlag}`}</code> })}</span>
        </div>
      )}
      {state === 'paused' && (
        <div className="alert alert-error" role="alert">
          <strong>{t('fn.pausedTitle')}</strong>
          <span>{t('fn.pausedBody')}</span>
          <Link to="/money/prepaid" className="btn btn-sm btn-danger">
            {t('pp.topUp')}
          </Link>
        </div>
      )}

      {/* 実行基盤の無いサーバーでは URL・資源・上限は意味を持たないので出さない（案内だけにする） */}
      {s && state !== 'unavailable' && (
        <>
          <section className="card live-card">
            <div>
              <span className="sidebar-label">{t('fn.url')}</span>
              {s.url ? (
                <a className="live-url" href={s.url} target="_blank" rel="noreferrer noopener">
                  {s.url} ↗
                </a>
              ) : (
                <p className="muted small">{t('fn.noUrl')}</p>
              )}
            </div>
            <div className="live-version">
              {s.deployed ? (
                <>
                  <span className="live-v">v{s.version}</span>
                  <span className="small muted">{s.deployedAt && t('fn.deployedAgo', { ago: ago(s.deployedAt) })}</span>
                </>
              ) : (
                <span className="muted small">{tr('fn.notDeployed', { cmd: <code>{cliCmd}</code> })}</span>
              )}
            </div>
          </section>

          <div className="money-grid">
            <section className="card">
              <h2 className="card-title">{t('fn.data')}</h2>
              {s.resources.length === 0 ? (
                <p className="muted small">{t('fn.noData')}</p>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('fn.col.binding')}</th>
                        <th>{t('fn.col.kind')}</th>
                        <th>{t('fn.col.created')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.resources.map((r) => (
                        <tr key={`${r.kind}-${r.binding}`}>
                          <td className="mono">{r.binding}</td>
                          <td>{kindLabel(r.kind)}</td>
                          <td title={fullDate(r.createdAt)}>{ago(r.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <h3 className="section-title">{t('fn.crons')}</h3>
              {s.crons.length ? (
                <ul className="chips">
                  {s.crons.map((c) => (
                    <li key={c} className="chip mono">
                      {c}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted small">{t('fn.none')}</p>
              )}
            </section>

            {isOwner && <LimitsCard base={base} limits={s.limits} ranges={s.limitRanges} onSaved={() => st.reload()} />}
          </div>

          {/* API キーやパスワードを入れる・上書き・削除（2026-10-04 console/secret-input）。AI は値を受けず、ここ（#secrets・?secret=NAME）へのリンクを返す */}
          <FunctionSecretsCard base={base} env={env} secrets={s.secrets} onChanged={() => st.reload()} />
        </>
      )}

      {state !== 'unavailable' && (
        <section className="card">
          <h2 className="card-title">{t('fn.deploys')}</h2>
          {deps.error && !deps.error.notAvailable && <ErrorState error={deps.error} what={t('fn.deploys')} onRetry={deps.reload} />}
          {deps.data && deps.data.deployments.length === 0 && (
            <p className="muted small">
              {t('fn.noDeploys')} {tr('fn.noDeploysCmd', { cmd: <code>{CLI} functions deploy --env {env}</code> })}
            </p>
          )}
          {deps.data && deps.data.deployments.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('fn.col.version')}</th>
                    <th>{t('fn.col.message')}</th>
                    <th className="num">{t('fn.col.size')}</th>
                    <th>{t('fn.col.when')}</th>
                  </tr>
                </thead>
                <tbody>
                  {deps.data.deployments.map((d) => (
                    <tr key={d.version}>
                      <td>
                        <strong>v{d.version}</strong>
                        {s?.version === d.version && <span className="pill pill-accent">{t('hosting.livePill')}</span>}
                      </td>
                      <td className="small">{d.message ?? <span className="muted">{t('hosting.noMessage')}</span>}</td>
                      <td className="num">{bytes(d.bytes)}</td>
                      <td title={fullDate(d.createdAt)}>{ago(d.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <LoadMore more={deps.more} />
        </section>
      )}

      <ConfirmDialog
        open={confirmOff}
        title={t('fn.turnOffTitle', { env: envName })}
        confirmLabel={t('fn.turnOff')}
        tone={env === 'production' ? 'danger' : 'default'}
        busy={busy}
        onClose={() => setConfirmOff(false)}
        onConfirm={() => void toggle(false)}
      >
        <p>{t('fn.turnOffBody')}</p>
      </ConfirmDialog>
    </>
  );
}

function LimitsCard({ base, limits, ranges, onSaved }: { base: string; limits: FunctionsStatus['limits']; ranges?: LimitRanges; onSaved: () => void }) {
  const { t } = useT();
  // 選んだ値（requested）から始める = 残高で切った値で上書き保存しない（functionsLimits.ts limitsFormStart の WHY）
  const start = limitsFormStart(limits);
  const [cpu, setCpu] = useState(String(start.cpuMs));
  const [sub, setSub] = useState(String(start.subRequests));
  const short = balanceShortfall(limits);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      await api(`${base}/limits`, { method: 'PUT', body: { cpuMs: Number(cpu), subRequests: Number(sub) } });
      setMsg({ ok: true, text: t('fn.limitsSaved') });
      onSaved();
    } catch (e2) {
      setMsg({ ok: false, text: errorText(e2 as ApiError) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <h2 className="card-title">{t('fn.limits')}</h2>
      <div className="form-grid form-grid-top">
        {/* 範囲はサーバー（GET …/functions の limitRanges = compute/serviceCore.ts の LIMITS）から出す。数字を画面に写さない:
            以前は max={30000} を写していて、サーバーが 5,000ms に下げた後も 30,000 まで入れられた（保存で断られた）。
            2026-10-04 console/limits-ui・洗い出し L003 */}
        <label className="field">
          <span>
            {t('fn.cpu')}
            <RangeNote range={limitInputRange(ranges, 'cpuMs', limits.max)} />
          </span>
          <input type="number" {...limitInputRange(ranges, 'cpuMs', limits.max)} value={cpu} onChange={(e) => setCpu(e.target.value)} />
        </label>
        <label className="field">
          <span>
            {t('fn.subRequests')}
            <RangeNote range={limitInputRange(ranges, 'subRequests', limits.max)} />
          </span>
          <input type="number" {...limitInputRange(ranges, 'subRequests', limits.max)} value={sub} onChange={(e) => setSub(e.target.value)} />
        </label>
      </div>
      <p className="muted small">{t('fn.limitsHint')}</p>
      {/* 選んだ CPU が今の残高の上限より大きい = 一部しか効いていない。数字はサーバーの cpuMsCap・requiredBalance（式を画面に写さない）。
          残高が増えると 1 分ほどで自動で上がる（compute/limitsFollow.ts）ので「入金すれば効く」とだけ言う。
          前払いの単位は usd だけ（money/prepaid.ts PREPAID_UNIT・UsagePage.tsx と同じ）。2026-10-05 console/followups */}
      {short && (
        <p className="alert alert-warn small">
          {t('fn.limitsBalance', { cap: formatNumber(short.capMs), want: formatNumber(short.wantMs), balance: money(short.requiredBalance, 'usd') })}{' '}
          <Link to="/money/prepaid">{t('fn.limitsBalanceLink')} →</Link>
        </p>
      )}
      {msg && (
        <p className={`alert ${msg.ok ? 'alert-ok' : 'alert-error'}`} role="status">
          {msg.text}
        </p>
      )}
      <div className="form-actions">
        <button className="btn btn-ghost" disabled={busy}>
          {busy ? t('common.working') : t('fn.saveLimits')}
        </button>
      </div>
    </form>
  );
}

/** 「（1〜5,000）」の注記。サーバーが範囲を返さない（古いサーバー）ときは出さない = 古い数字を見せない */
function RangeNote({ range }: { range?: { min?: number; max?: number } }) {
  const { t } = useT();
  if (range?.min === undefined || range.max === undefined) return null;
  return <span className="muted small"> {t('fn.limitRange', { min: formatNumber(range.min), max: formatNumber(range.max) })}</span>;
}
